// web/src/contact/ContactForm.tsx
//
// Renders the contact form fields + handles submit. Two modes based on
// whether `prefilledEmail` is provided:
// - Anonymous (no prefilledEmail): renders TurnstileWidget; submit is
//   gated on a valid token.
// - Authenticated (prefilledEmail set): no Turnstile; email field is
//   disabled (read-only); name field is prefilled but editable.
//
// On a successful submit, the form swaps to an inline success state
// with a "Send another message" button that resets all state.
import { useRef, useState } from "react";
import type { ContactPayload, ContactTopic } from "./api";
import { submitContact } from "./api";
import { TurnstileWidget, type TurnstileWidgetHandle } from "./TurnstileWidget";

type Props = {
  prefilledEmail?: string;
  prefilledName?: string;
};

const TOPIC_OPTIONS: { value: ContactTopic; label: string }[] = [
  { value: "privacy", label: "Privacy or data request" },
  { value: "support", label: "Account or technical support" },
  { value: "billing", label: "Billing question" },
  { value: "other", label: "Other" },
];

const MESSAGE_MAX = 5000;
const MESSAGE_COUNTER_THRESHOLD = 4500;

type ApiErrorLike = { status?: number };

function classifyError(err: unknown): { kind: "rate_limited" | "captcha_failed" | "generic"; status?: number } {
  const e = err as ApiErrorLike;
  const status = e?.status;
  if (status === 429) return { kind: "rate_limited", status };
  if (status === 422) return { kind: "captcha_failed", status };
  return { kind: "generic", status };
}

export function ContactForm({ prefilledEmail, prefilledName }: Props) {
  const isAuthed = prefilledEmail !== undefined;

  const [name, setName] = useState(prefilledName ?? "");
  const [email] = useState(prefilledEmail ?? "");
  const [emailUnauthed, setEmailUnauthed] = useState("");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState<ContactTopic | "">("");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedTo, setSubmittedTo] = useState<string | null>(null);

  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const effectiveEmail = isAuthed ? email : emailUnauthed;

  const canSubmit =
    !submitting &&
    effectiveEmail.length > 0 &&
    topic !== "" &&
    message.length > 0 &&
    message.length <= MESSAGE_MAX &&
    (isAuthed || turnstileToken !== null);

  function reset() {
    setName(prefilledName ?? "");
    setEmailUnauthed("");
    setSubject("");
    setTopic("");
    setMessage("");
    setTurnstileToken(null);
    setError(null);
    setSubmittedTo(null);
    setSubmitting(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const payload: ContactPayload = {
      email: effectiveEmail,
      topic: topic as ContactTopic,
      message,
    };
    if (name) payload.name = name;
    if (subject) payload.subject = subject;
    if (!isAuthed && turnstileToken) payload.turnstileToken = turnstileToken;

    try {
      await submitContact(payload);
      setSubmittedTo(effectiveEmail);
    } catch (err) {
      const classified = classifyError(err);
      if (classified.kind === "rate_limited") {
        setError("You've sent several messages recently. Please try again later.");
      } else if (classified.kind === "captcha_failed") {
        setError("Captcha verification failed. Please try again.");
        setTurnstileToken(null);
        turnstileRef.current?.reset();
      } else {
        setError(
          "Something went wrong. Please try again or email us directly at support@tapelens.com.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submittedTo) {
    return (
      <div className="space-y-4 rounded-md border p-6 text-sm">
        <h2 className="text-lg font-semibold">✓ Thanks for reaching out</h2>
        <p className="text-muted-foreground">
          We've received your message and will respond within 5 business days at{" "}
          <span className="font-mono">{submittedTo}</span>.
        </p>
        <button
          type="button"
          onClick={reset}
          className="text-sm underline underline-offset-4 hover:text-foreground"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
      {error && (
        <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-1">
        <label htmlFor="contact-name" className="text-sm font-medium">
          Name <span className="text-muted-foreground text-xs">(optional)</span>
        </label>
        <input
          id="contact-name"
          type="text"
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="contact-email" className="text-sm font-medium">
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          required
          maxLength={254}
          disabled={isAuthed}
          value={effectiveEmail}
          onChange={(e) => !isAuthed && setEmailUnauthed(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm disabled:bg-muted disabled:cursor-not-allowed"
        />
        {isAuthed && (
          <p className="text-xs text-muted-foreground italic">Uses your account email.</p>
        )}
      </div>

      <div className="space-y-1">
        <label htmlFor="contact-subject" className="text-sm font-medium">
          Subject <span className="text-muted-foreground text-xs">(optional)</span>
        </label>
        <input
          id="contact-subject"
          type="text"
          maxLength={200}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="contact-topic" className="text-sm font-medium">
          Topic
        </label>
        <select
          id="contact-topic"
          required
          value={topic}
          onChange={(e) => setTopic(e.target.value as ContactTopic | "")}
          className="w-full rounded-md border px-3 py-2 text-sm bg-background"
        >
          <option value="">Select a topic…</option>
          {TOPIC_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label htmlFor="contact-message" className="text-sm font-medium">
          Message
        </label>
        <textarea
          id="contact-message"
          required
          rows={6}
          maxLength={MESSAGE_MAX}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-md border px-3 py-2 text-sm resize-y"
        />
        {message.length > MESSAGE_COUNTER_THRESHOLD && (
          <p className="text-xs text-muted-foreground">
            {message.length} / {MESSAGE_MAX}
          </p>
        )}
      </div>

      {!isAuthed && (
        <TurnstileWidget
          ref={turnstileRef}
          onVerify={(t) => setTurnstileToken(t)}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
        />
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
